const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    if (!callback) return;
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
};

/**
 * A web component for slideshows
 * See plugins/castiron/marinemammalcenter/components/hero/slider.htm
 */
class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    const self = this;

    this.autoplayTimeoutFn = null;

    this.observer = null;

    this.reduceMotion =
      window.matchMedia(`(prefers-reduced-motion: reduce)`) === true ||
      window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

    const autoplay = this.dataset.autoplay === "true" && !this.reduceMotion;

    this.abortController = new AbortController();

    self.state = new Proxy(
      {
        active: 0,
        autoplay,
      },
      {
        set(state, key, value) {
          const oldValue = state[key];

          state[key] = value;
          if (oldValue !== value) {
            switch (key) {
              case "active":
                self.processActiveChange();
                break;
              case "autoplay":
                self.processAutoplayChange();
                break;
              default:
                break;
            }
          }
          return state;
        },
      }
    );
  }

  connectedCallback() {
    // You're missing a slider! Do not pass go.
    if (!this.slider) {
      console.error(
        'Error initializing SlideshowElement: missing data-ref="slider".'
      );
      return;
    }

    this.count = this.slides.length || 0;

    // There are no slides. Remove all slideshow controls.
    if (this.count <= 1) {
      this.removeControls();
      return;
    }

    // You're missing controls.
    if (!this.nextButton && !this.prevButton && !this.navLinks) {
      console.warn("Warning: SlideshowElement is missing controls.");
    }

    this.bindListeners();
    this.updateNav();
    this.updateSlides();
    if (this.state.autoplay) this.processAutoplayChange();
  }

  disconnectedCallback() {
    this.unbindListeners();
  }

  get slider() {
    return this.querySelector("[data-ref='slider']");
  }

  get slides() {
    return this.slider.children;
  }

  get nextButton() {
    return this.querySelectorAll("[data-ref='nextButton']");
  }

  get prevButton() {
    return this.querySelectorAll("[data-ref='previousButton']");
  }

  get current() {
    return this.querySelector("[data-ref='current']");
  }

  get nav() {
    return this.querySelectorAll("[data-ref='nav']");
  }

  bindListeners() {
    // TODO: Abort controller to unbind
    this.nextButton?.forEach((el) => {
      el.addEventListener("click", this.onNextClick, {
        signal: this.abortController.signal,
      });
    });
    this.prevButton?.forEach((el) => {
      el.addEventListener("click", this.onPrevClick, {
        signal: this.abortController.signal,
      });
    });

    if (this.dataset.autoplay === "true") {
      this.addEventListener("mouseover", this.onMouseOver, {
        signal: this.abortController.signal,
      });
      this.addEventListener("mouseout", this.onMouseOut, {
        signal: this.abortController.signal,
      });
    }

    const options = {
      root: this.slider,
      rootMargin: "0px",
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver(this.onSlideIntersect, options);

    for (const slide of this.slides) {
      this.observer.observe(slide);
    }

    // If javascript has been turned off,
    // the hash links will take the user to the correct slide.
    this.nav?.forEach((el) => {
      const links = el.querySelectorAll("a");
      links.forEach((link, i) => {
        link.addEventListener("click", this.onNavLinkClick, {
          signal: this.abortController.signal,
        });
        link.dataset.slideIndex = i;
      });
    });
  }

  unbindListeners() {
    // remove all event listeners
    this.abortController.abort();

    if (this.observer && this.slides) {
      for (const slide of this.slides) {
        this.observer.unobserve(slide);
      }
    }

    if (this.autoplayTimeoutFn) clearInterval(this.autoplayTimeoutFn);
  }

  onNavLinkClick = (e) => {
    e.preventDefault();
    const index = parseInt(e.target.dataset.slideIndex, 10) || 0;
    this.state.active = index;
    this.state.autoplay = false;
    this.scrollToIndex(index);
  };

  onNextClick = () => {
    this.next();
    this.state.autoplay = false;
  };

  onPrevClick = () => {
    this.prev();
    this.state.autoplay = false;
  };

  onMouseOver = () => {
    this.state.autoplay = false;
  };

  onMouseOut = () => {
    if (!this.reduceMotion) {
      this.state.autoplay = true;
    }
  };

  onSlideIntersect = debounce((entries, observer) => {
    entries.forEach((entry) => {
      const target = entry.target;
      if (!entry.isIntersecting) return;

      const index = Array.from(target.parentElement.children).indexOf(target);

      if (index >= 0) {
        this.state.active = index;
      }
    });
  }, 300);

  // Move to previous slide
  prev = () => {
    let activeNext = this.state.active - 1 || 0;

    if (activeNext < 0) {
      activeNext = this.count - 1;
    }

    this.state.active = activeNext;
    this.scrollToIndex(activeNext);
  };

  // Move to next slide
  next = () => {
    let activeNext = this.state.active + 1;

    if (activeNext >= this.count) {
      activeNext = 0;
    }

    this.state.active = activeNext;
    this.scrollToIndex(activeNext);
  };

  processAutoplayChange() {
    if (this.state.autoplay && !this.reduceMotion) {
      this.autoplayTimeoutFn = setInterval(this.next, 6250);
    } else if (this.autoplayTimeoutFn) {
      clearInterval(this.autoplayTimeoutFn);
    }
  }

  // data-active attributes added for styling, debugging
  processActiveChange() {
    this.updateCurrent();
    this.updateNav();
    this.updateSlides();
  }

  updateCurrent() {
    if (this.current) {
      this.current.innerHTML = this.state.active + 1;
    }
  }

  updateNav() {
    this.nav?.forEach((el) => {
      const links = el.querySelectorAll("a");
      links?.forEach((link, i) => {
        link.dataset.active = i === this.state.active;
      });
    });
  }

  updateSlides() {
    for (let i = 0; i < this.slides.length; i++) {
      const slide = this.slides[i];
      const isActive = i === this.state.active;

      if (isActive) {
        slide.removeAttribute("inert");
      } else {
        slide.setAttribute("inert", "");
      }

      slide.dataset.active = isActive;
    }
  }

  scrollToIndex(index) {
    this.slider.scrollTo({
      left: index * this.slider.clientWidth,
      top: 0,
    });
  }

  removeControls() {
    if (this.nextButton) this.nextButton.remove();
    if (this.prevButton) this.prevButton.remove();
    if (this.navLinks) this.navLinks.remove();
  }
}

customElements.define("slideshow-element", SlideshowElement);
